import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../shared/services/apiClient";
import { ConfirmModal, useToast } from "../../../shared/components/SharedUI";
import {
  getResidentes, crearResidente, updateResidente, deleteResidente,
} from "../services/portalService";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useBreakpoint() {
  const get = () => window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const fn = () => setBp(get());
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return bp;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function initials(r) {
  const f = (r.first_name || r.username || "?")[0].toUpperCase();
  const l = (r.last_name || "")[0]?.toUpperCase() || "";
  return f + l;
}
const AVATAR_COLORS = [
  "#f97316","#3b82f6","#10b981","#8b5cf6","#ef4444","#f59e0b","#06b6d4","#ec4899",
];
function avatarColor(str) {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) h = (str.charCodeAt(i) + ((h << 5) - h));
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
const PAGE_SIZE = 12;

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color = "#f97316" }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border-card)",
      borderRadius: 12, padding: "16px 20px", display: "flex",
      alignItems: "center", gap: 14, flex: "1 1 140px", minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: `${color}18`, border: `1px solid ${color}30`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color, fontSize: 16,
      }}>
        <i className={`fa-solid ${icon}`} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 3, fontWeight: 500, whiteSpace: "nowrap" }}>{label}</div>
      </div>
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ page, total, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const btn = (disabled, onClick, children) => (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "6px 12px", borderRadius: 8,
      border: "1px solid var(--border-card)",
      background: "var(--bg-card)",
      color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
      cursor: disabled ? "default" : "pointer", fontSize: 13, opacity: disabled ? 0.4 : 1,
    }}>
      {children}
    </button>
  );
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "12px 16px", borderTop: "1px solid var(--border-card)",
    }}>
      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
        {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {btn(page === 1, () => onChange(page - 1), <i className="fa-solid fa-chevron-left" />)}
        <span style={{
          padding: "5px 12px", borderRadius: 8, fontSize: 13,
          background: "#f97316", color: "#fff", fontWeight: 700,
        }}>
          {page} / {pages}
        </span>
        {btn(page === pages, () => onChange(page + 1), <i className="fa-solid fa-chevron-right" />)}
      </div>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
function ResidenteModal({ modal, form, setForm, filiales, onSave, onClose, saving, err }) {
  const { t } = useTranslation();
  const [showPass, setShowPass] = useState(false);
  const isEdit = modal.mode === "edit";

  const fld = (key, label, type = "text", required = false, placeholder = "", disabled = false) => (
    <div>
      <label style={{
        fontSize: 11, fontWeight: 700, color: "var(--text-secondary)",
        display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5,
      }}>
        {label}{required && <span style={{ color: "#f87171" }}> *</span>}
      </label>
      <input
        type={type} value={form[key] ?? ""} disabled={disabled}
        placeholder={placeholder}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "9px 12px", borderRadius: 8,
          border: "1px solid var(--border-card)",
          background: disabled ? "var(--hover-bg)" : "var(--bg-input, var(--hover-bg))",
          color: "var(--text-primary)", fontSize: 13, outline: "none",
          opacity: disabled ? 0.6 : 1,
        }}
      />
    </div>
  );

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(4px)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 16, width: "min(580px, 96vw)",
        boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        maxHeight: "92vh", overflowY: "auto", display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: isEdit ? "rgba(59,130,246,0.15)" : "rgba(249,115,22,0.15)",
              border: `1px solid ${isEdit ? "rgba(59,130,246,0.3)" : "rgba(249,115,22,0.3)"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: isEdit ? "#3b82f6" : "#f97316", fontSize: 16,
            }}>
              <i className={`fa-solid ${isEdit ? "fa-user-pen" : "fa-user-plus"}`} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>
                {isEdit ? t("portal.editarResidente") : t("portal.crearResidente")}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
                {isEdit ? "Modificá los datos del acceso al portal" : "Configurá el acceso al portal del residente"}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: "var(--text-muted)",
            cursor: "pointer", fontSize: 18, padding: 4, lineHeight: 1,
          }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Acceso */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#f97316",
              textTransform: "uppercase", letterSpacing: 1, marginBottom: 12,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <i className="fa-solid fa-key" style={{ fontSize: 10 }} /> Credenciales de acceso
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
              {fld("username", t("portal.usuario"), "text", true, "ej: filial_10", isEdit)}
              <div>
                <label style={{
                  fontSize: 11, fontWeight: 700, color: "var(--text-secondary)",
                  display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5,
                }}>
                  {isEdit ? t("portal.nuevaContrasena") : t("portal.contrasena")}
                  {!isEdit && <span style={{ color: "#f87171" }}> *</span>}
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPass ? "text" : "password"}
                    value={form.password ?? ""}
                    placeholder={isEdit ? t("portal.dejarVacioMantener") : "Mínimo 8 caracteres"}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    style={{
                      width: "100%", boxSizing: "border-box",
                      padding: "9px 36px 9px 12px", borderRadius: 8,
                      border: "1px solid var(--border-card)",
                      background: "var(--bg-input, var(--hover-bg))",
                      color: "var(--text-primary)", fontSize: 13, outline: "none",
                    }}
                  />
                  <button type="button" onClick={() => setShowPass(p => !p)} style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", color: "var(--text-secondary)",
                    cursor: "pointer", padding: 0, fontSize: 13,
                  }}>
                    <i className={`fa-solid ${showPass ? "fa-eye-slash" : "fa-eye"}`} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Datos personales */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#f97316",
              textTransform: "uppercase", letterSpacing: 1, marginBottom: 12,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <i className="fa-solid fa-user" style={{ fontSize: 10 }} /> Datos personales
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
              {fld("first_name", t("portal.nombre"), "text", false, "Nombre(s)")}
              {fld("last_name", t("portal.apellido"), "text", false, "Apellido(s)")}
            </div>
            <div style={{ marginTop: 12 }}>
              {fld("email", t("portal.correo"), "email", false, "correo@ejemplo.com")}
            </div>
          </div>

          {/* Filial */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#f97316",
              textTransform: "uppercase", letterSpacing: 1, marginBottom: 12,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <i className="fa-solid fa-building" style={{ fontSize: 10 }} /> Asignación
            </div>
            <label style={{
              fontSize: 11, fontWeight: 700, color: "var(--text-secondary)",
              display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5,
            }}>
              {t("portal.filial")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <select
              value={form.filial_id ?? ""}
              onChange={e => setForm(f => ({ ...f, filial_id: e.target.value }))}
              style={{
                width: "100%", padding: "9px 12px", borderRadius: 8,
                border: "1px solid var(--border-card)",
                background: "var(--bg-input, var(--hover-bg))",
                color: "var(--text-primary)", fontSize: 13, outline: "none",
              }}
            >
              <option value="">{t("portal.seleccionarFilial")}</option>
              {filiales.map(f => (
                <option key={f.id} value={f.id}>
                  Filial {f.idFilial}{f.propietario_nombre ? ` — ${f.propietario_nombre}` : ""}
                </option>
              ))}
            </select>
          </div>

          {err && (
            <div style={{
              background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
              borderLeft: "3px solid #f87171", borderRadius: 8,
              padding: "10px 14px", color: "#f87171", fontSize: 13,
              display: "flex", alignItems: "flex-start", gap: 8,
            }}>
              <i className="fa-solid fa-circle-exclamation" style={{ marginTop: 1, flexShrink: 0 }} />
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 24px 20px", borderTop: "1px solid var(--border-card)",
          display: "flex", gap: 10, justifyContent: "flex-end",
        }}>
          <button onClick={onClose} style={{
            padding: "9px 20px", borderRadius: 8,
            border: "1px solid var(--border-card)",
            background: "transparent", color: "var(--text-secondary)",
            cursor: "pointer", fontSize: 14, fontWeight: 500,
          }}>
            {t("common.cancelar")}
          </button>
          <button onClick={onSave} disabled={saving} style={{
            padding: "9px 22px", borderRadius: 8, border: "none",
            background: saving ? "#b45309" : "#f97316",
            color: "#fff", fontWeight: 700, fontSize: 14,
            cursor: saving ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            {saving
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
              : <><i className="fa-solid fa-floppy-disk" /> {t("common.guardar")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AdminResidentes() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();
  const bp = useBreakpoint();
  const isMobile = bp === "mobile";
  const isTablet = bp === "tablet";

  const [residentes, setResidentes] = useState([]);
  const [filiales,   setFiliales]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null);
  const [form,       setForm]       = useState({});
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState(null);
  const [confirm,    setConfirm]    = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  const [search,       setSearch]       = useState("");
  const [filterFilial, setFilterFilial] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [page,         setPage]         = useState(1);

  function load() {
    setLoading(true);
    getResidentes()
      .then(setResidentes)
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/")
      .then(r => r.json())
      .then(d => setFiliales(d.results || d))
      .catch(() => {});
  }, []);

  // Stats
  const totalActivos      = residentes.filter(r => r.is_active !== false).length;
  const totalInactivos    = residentes.filter(r => r.is_active === false).length;
  const filialesConAcceso = new Set(residentes.filter(r => r.filial_id).map(r => r.filial_id)).size;

  // Filter
  const filtered = residentes.filter(r => {
    if (search) {
      const q = search.toLowerCase();
      const full = `${r.first_name || ""} ${r.last_name || ""} ${r.username} ${r.email || ""}`.toLowerCase();
      if (!full.includes(q)) return false;
    }
    if (filterFilial && String(r.filial_id) !== filterFilial) return false;
    if (filterEstado === "activo"   && r.is_active === false) return false;
    if (filterEstado === "inactivo" && r.is_active !== false) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasFilter = search || filterFilial || filterEstado;

  function openCreate() {
    setForm({ username: "", email: "", first_name: "", last_name: "", password: "", filial_id: "" });
    setErr(null); setModal({ mode: "create" });
  }
  function openEdit(r) {
    setForm({ email: r.email, first_name: r.first_name, last_name: r.last_name, filial_id: r.filial_id, password: "" });
    setErr(null); setModal({ mode: "edit", id: r.id });
  }

  async function handleSave() {
    setSaving(true); setErr(null);
    try {
      if (modal.mode === "create") {
        await crearResidente({ ...form, filial_id: parseInt(form.filial_id) });
      } else {
        const payload = { ...form, filial_id: parseInt(form.filial_id) };
        if (!payload.password) delete payload.password;
        await updateResidente(modal.id, payload);
      }
      showToast(t("common.guardado"), true);
      setModal(null); load();
    } catch (e) {
      try { const d = JSON.parse(e.message); setErr(Object.values(d).flat().join(". ")); }
      catch  { setErr(e.message); }
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteResidente(confirm.id);
      showToast(t("common.eliminado"), true);
      setConfirm(null); load();
    } catch {
      showToast(t("common.errorEliminar"), false);
    } finally { setDeleting(false); }
  }

  const inputStyle = {
    padding: "8px 12px", borderRadius: 8,
    border: "1px solid var(--border-card)",
    background: "var(--hover-bg)", color: "var(--text-primary)",
    fontSize: 13, outline: "none",
  };

  const TH = ({ children, align = "left", hide }) => {
    if (hide === "mobile" && isMobile) return null;
    if (hide === "tablet" && (isMobile || isTablet)) return null;
    return (
      <th style={{
        padding: "11px 14px", fontSize: 11, fontWeight: 700, textAlign: align,
        color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.5,
        background: "var(--hover-bg)", borderBottom: "1px solid var(--border-card)",
        whiteSpace: "nowrap",
      }}>
        {children}
      </th>
    );
  };

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      {toastEl}
      <ConfirmModal
        open={!!confirm} name={confirm?.name}
        onConfirm={handleDelete} onCancel={() => setConfirm(null)} loading={deleting}
      />

      {/* Breadcrumb */}
      <div style={{ marginBottom: 6 }}>
        <Link to="/" style={{ textDecoration: "none", color: "var(--text-secondary)", fontSize: 13 }}>
          Portal Admin
        </Link>
        <span style={{ color: "var(--text-secondary)", fontSize: 13 }}> / </span>
        <span style={{ color: "var(--text-primary)", fontWeight: 600, fontSize: 13 }}>
          {t("portal.residentes")}
        </span>
      </div>

      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 22,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 800, color: "var(--text-primary)" }}>
            {t("portal.residentes")}
          </h2>
          <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 13 }}>
            Gestioná los accesos al portal de residentes
          </p>
        </div>
        <button onClick={openCreate} style={{
          background: "#f97316", color: "#fff", border: "none", borderRadius: 10,
          padding: "10px 18px", cursor: "pointer", fontWeight: 700, fontSize: 13,
          display: "flex", alignItems: "center", gap: 7,
          boxShadow: "0 2px 8px rgba(249,115,22,0.35)",
        }}>
          <i className="fa-solid fa-user-plus" />
          {!isMobile && t("portal.nuevo")}
          {isMobile && "Nuevo"}
        </button>
      </div>

      {/* Stats */}
      {!loading && residentes.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
          <StatCard icon="fa-users"        label="Total registrados"   value={residentes.length} color="#f97316" />
          <StatCard icon="fa-circle-check" label="Cuentas activas"     value={totalActivos}       color="#10b981" />
          <StatCard icon="fa-circle-xmark" label="Cuentas inactivas"   value={totalInactivos}     color="#6b7280" />
          <StatCard icon="fa-building"     label="Filiales con acceso" value={filialesConAcceso}  color="#3b82f6" />
        </div>
      )}

      {/* Table card */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 14,
      }}>
        {/* Filter bar */}
        <div style={{
          padding: "12px 14px", borderBottom: "1px solid var(--border-card)",
          display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
          borderRadius: "14px 14px 0 0",
        }}>
          <div style={{ position: "relative", flex: "1 1 200px", minWidth: 0 }}>
            <i className="fa-solid fa-magnifying-glass" style={{
              position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
              color: "var(--text-secondary)", fontSize: 12, pointerEvents: "none",
            }} />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar por nombre, usuario o correo…"
              style={{ ...inputStyle, paddingLeft: 30, width: "100%", boxSizing: "border-box" }}
            />
          </div>
          {!isMobile && (
            <select
              value={filterFilial}
              onChange={e => { setFilterFilial(e.target.value); setPage(1); }}
              style={{ ...inputStyle, flex: "0 1 150px" }}
            >
              <option value="">Todas las filiales</option>
              {filiales.map(f => (
                <option key={f.id} value={f.id}>Filial {f.idFilial}</option>
              ))}
            </select>
          )}
          <select
            value={filterEstado}
            onChange={e => { setFilterEstado(e.target.value); setPage(1); }}
            style={{ ...inputStyle, flex: "0 1 120px" }}
          >
            <option value="">Todos</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
          {hasFilter && (
            <button
              onClick={() => { setSearch(""); setFilterFilial(""); setFilterEstado(""); setPage(1); }}
              style={{
                padding: "7px 10px", borderRadius: 8, border: "none",
                background: "rgba(249,115,22,0.1)", color: "#f97316",
                cursor: "pointer", fontSize: 12, fontWeight: 700,
                display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
              }}
            >
              <i className="fa-solid fa-xmark" /> Limpiar
            </button>
          )}
          <span style={{
            marginLeft: "auto", fontSize: 12, color: "var(--text-secondary)",
            background: "var(--hover-bg)", borderRadius: 20,
            padding: "4px 10px", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
          }}>
            {filtered.length} residente{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-secondary)" }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: 24, marginBottom: 12, display: "block" }} />
            Cargando residentes…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <div style={{
              width: 60, height: 60, borderRadius: "50%",
              background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px", color: "#f97316", fontSize: 24,
            }}>
              <i className="fa-solid fa-users-slash" />
            </div>
            <p style={{ margin: 0, fontWeight: 700, color: "var(--text-primary)", fontSize: 15 }}>
              {residentes.length === 0 ? "No hay residentes registrados" : "Sin resultados"}
            </p>
            <p style={{ margin: "6px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
              {residentes.length === 0
                ? "Creá el primer acceso para un residente."
                : "Probá cambiando los filtros de búsqueda."}
            </p>
            {residentes.length === 0 && (
              <button onClick={openCreate} style={{
                marginTop: 16, background: "#f97316", color: "#fff", border: "none",
                borderRadius: 8, padding: "9px 18px", cursor: "pointer", fontWeight: 700, fontSize: 13,
              }}>
                <i className="fa-solid fa-plus" style={{ marginRight: 6 }} />
                Crear primer residente
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <TH>Residente</TH>
                    <TH hide="mobile">Usuario</TH>
                    <TH hide="tablet">Correo</TH>
                    <TH align="center">Filial</TH>
                    <TH align="center">Estado</TH>
                    <TH align="center">Acciones</TH>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((r, i) => {
                    const fullName = `${r.first_name || ""} ${r.last_name || ""}`.trim() || "—";
                    const ini      = initials(r);
                    const color    = avatarColor(r.username || fullName);
                    const isActive = r.is_active !== false;
                    const filial   = filiales.find(f => f.id === r.filial_id || f.id === Number(r.filial_id));
                    return (
                      <tr
                        key={r.id}
                        style={{
                          borderBottom: i < paged.length - 1 ? "1px solid var(--border-card)" : "none",
                          transition: "background 0.12s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--hover-bg)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        {/* Residente (avatar + nombre) */}
                        <td style={{ padding: "12px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{
                              width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                              background: `${color}22`, border: `2px solid ${color}44`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              color, fontWeight: 800, fontSize: 12,
                            }}>
                              {ini}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)", whiteSpace: "nowrap" }}>
                                {fullName}
                              </div>
                              {isMobile && (
                                <div style={{ fontSize: 11, color: "var(--text-secondary)", fontFamily: "monospace", marginTop: 2 }}>
                                  @{r.username}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Usuario */}
                        {!isMobile && (
                          <td style={{ padding: "12px 14px" }}>
                            <span style={{
                              background: "var(--hover-bg)", borderRadius: 6,
                              padding: "3px 8px", fontSize: 12,
                              color: "var(--text-secondary)", fontFamily: "monospace",
                              whiteSpace: "nowrap",
                            }}>
                              @{r.username}
                            </span>
                          </td>
                        )}

                        {/* Correo */}
                        {!isMobile && !isTablet && (
                          <td style={{ padding: "12px 14px" }}>
                            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                              {r.email || <span style={{ opacity: 0.4 }}>—</span>}
                            </span>
                          </td>
                        )}

                        {/* Filial */}
                        <td style={{ padding: "12px 14px", textAlign: "center" }}>
                          {filial ? (
                            <span style={{
                              background: "rgba(249,115,22,0.1)",
                              border: "1px solid rgba(249,115,22,0.25)",
                              color: "#f97316", borderRadius: 20,
                              padding: "3px 10px", fontSize: 12, fontWeight: 700,
                              whiteSpace: "nowrap", display: "inline-block",
                            }}>
                              Filial {filial.idFilial}
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>—</span>
                          )}
                        </td>

                        {/* Estado */}
                        <td style={{ padding: "12px 14px", textAlign: "center" }}>
                          <span style={{
                            background: isActive ? "rgba(16,185,129,0.1)" : "rgba(107,114,128,0.1)",
                            border: `1px solid ${isActive ? "rgba(16,185,129,0.3)" : "rgba(107,114,128,0.3)"}`,
                            color: isActive ? "#10b981" : "#6b7280",
                            borderRadius: 20, padding: "4px 10px",
                            fontSize: 11, fontWeight: 700,
                            whiteSpace: "nowrap", display: "inline-flex",
                            alignItems: "center", gap: 4,
                          }}>
                            <i className={`fa-solid ${isActive ? "fa-circle-check" : "fa-circle-xmark"}`}
                               style={{ fontSize: 10 }} />
                            {isActive ? "Activo" : "Inactivo"}
                          </span>
                        </td>

                        {/* Acciones */}
                        <td style={{ padding: "12px 14px", textAlign: "center" }}>
                          <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                            <button
                              onClick={() => openEdit(r)} title="Editar"
                              style={{
                                padding: "6px 10px", borderRadius: 7,
                                border: "1px solid rgba(59,130,246,0.3)",
                                background: "rgba(59,130,246,0.08)",
                                color: "#3b82f6", cursor: "pointer", fontSize: 12,
                              }}
                            >
                              <i className="fa-solid fa-pen-to-square" />
                            </button>
                            <button
                              onClick={() => setConfirm({ id: r.id, name: fullName })} title="Eliminar"
                              style={{
                                padding: "6px 10px", borderRadius: 7,
                                border: "1px solid rgba(239,68,68,0.3)",
                                background: "rgba(239,68,68,0.08)",
                                color: "#ef4444", cursor: "pointer", fontSize: 12,
                              }}
                            >
                              <i className="fa-solid fa-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={page} total={filtered.length} onChange={setPage} />
          </>
        )}
      </div>

      {modal && (
        <ResidenteModal
          modal={modal} form={form} setForm={setForm}
          filiales={filiales} onSave={handleSave}
          onClose={() => setModal(null)} saving={saving} err={err}
        />
      )}
    </div>
  );
}
